/**
 * Tabs component with Dashboard3 styling
 *
 * @example
 * ```tsx
 * <Tabs>
 *   <TabList>
 *     <Tab>Tab 1</Tab>
 *     <Tab>Tab 2</Tab>
 *   </TabList>
 *   <TabPanels>
 *     <TabPanel>Content 1</TabPanel>
 *     <TabPanel>Content 2</TabPanel>
 *   </TabPanels>
 * </Tabs>
 * ```
 */

"use client";

import {
  Tabs as ChakraTabs,
  type TabsProps as ChakraTabsProps,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
} from "@chakra-ui/react";
import { useThemeTokens } from "@/lib/theme/tokens";

export interface TabsProps extends ChakraTabsProps {}

export function Tabs({ children, ...props }: TabsProps) {
  return <ChakraTabs {...props}>{children}</ChakraTabs>;
}

export { TabList, Tab, TabPanels, TabPanel };

// Styled Tab component
export function StyledTab({ ...props }: React.ComponentProps<typeof Tab>) {
  const theme = useThemeTokens();

  return (
    <Tab
      color={theme.text.secondary}
      _selected={{
        color: theme.accent.primary,
        borderBottomColor: theme.accent.primary,
        "& svg": { color: "inherit" },
        "& span": { color: "inherit" },
      }}
      sx={{
        "@media (hover: hover)": {
          "&:hover": {
            color: theme.text.primary,
          },
        },
      }}
      {...props}
    />
  );
}

